import { Link } from "@tanstack/react-router";
import {
  Boxes,
  Flag,
  LayoutDashboard,
  type LucideIcon,
  ScrollText,
  Settings,
  ShoppingBag,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// The nav frame for the console. Orders, Settings, Feature flags and Katalog are
// live tabs (epic #236 / PRD #237); a tab without a `to` renders as a disabled
// "wkrótce" placeholder for a slice not built yet.
interface NavItem {
  label: string;
  icon: LucideIcon;
  to?: string;
  // Highlight only on an exact path match. Section roots (e.g. Orders) want the
  // tab lit on their child routes too, so they opt out.
  exact?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Pulpit", icon: LayoutDashboard, to: "/", exact: true },
  { label: "Zamówienia", icon: ShoppingBag, to: "/orders" },
  { label: "Ustawienia", icon: Settings, to: "/settings" },
  { label: "Przełączniki funkcji", icon: Flag, to: "/feature-flags" },
  { label: "Katalog", icon: Boxes, to: "/catalog" },
  { label: "Dokumenty prawne", icon: ScrollText, to: "/legal-documents" },
];

function ActorBadge({ actor }: { actor: string }) {
  const initial = actor.trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-background p-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary">
        {initial}
      </div>
      <div className="min-w-0">
        <p className="text-[0.7rem] uppercase tracking-wide text-muted-foreground">
          Zalogowano jako
        </p>
        <p className="truncate text-sm font-medium" title={actor}>
          {actor}
        </p>
      </div>
    </div>
  );
}

export function AdminShell({ actor, children }: { actor: string; children: ReactNode }) {
  return (
    <div className="flex min-h-dvh bg-paper text-foreground">
      <aside className="sticky top-0 flex h-dvh w-64 shrink-0 flex-col self-start overflow-y-auto border-r bg-surface">
        <div className="flex h-16 items-center gap-2 border-b px-6">
          <span className="font-display text-lg font-semibold tracking-tight">pufimi</span>
          <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            panel
          </span>
        </div>

        <nav className="flex-1 space-y-1 p-3" aria-label="Nawigacja panelu">
          {NAV_ITEMS.map((item) =>
            item.to ? (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                activeProps={{ className: "bg-primary/10 text-primary hover:bg-primary/10" }}
                activeOptions={{ exact: item.exact ?? false }}
              >
                <item.icon className="size-4 shrink-0" />
                {item.label}
              </Link>
            ) : (
              <div
                key={item.label}
                className="flex cursor-default items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground/60"
                aria-disabled="true"
              >
                <item.icon className="size-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
                  wkrótce
                </span>
              </div>
            ),
          )}
        </nav>

        <div className="border-t p-3">
          <ActorBadge actor={actor} />
        </div>
      </aside>

      <main className={cn("flex-1 overflow-x-auto")}>
        <div className="mx-auto max-w-4xl px-8 py-10">{children}</div>
      </main>
    </div>
  );
}
